export interface GuideArticle {
  articleNumber: string;
  title: string;
  body: string;
}

export interface GuideSection {
  name: string;
  articles: GuideArticle[];
  subsections: { name: string; articles: GuideArticle[] }[];
}

export interface GuestGuide {
  available: boolean;
  guide: { sections: GuideSection[] };
  property_facts: {
    entry_instructions?: string;
    parking?: string;
    laundry_facilities?: string;
    directions?: string;
    ge_ta_license?: string;
  };
}

export type GuestGuideArgs =
  | { reservationName: string; listingId?: never }
  | { listingId: string; reservationName?: never };

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isArticle(value: unknown): boolean {
  return (
    isObject(value) &&
    typeof value.articleNumber === 'string' &&
    typeof value.title === 'string' &&
    typeof value.body === 'string'
  );
}

function isArticleGroup(value: unknown): value is Record<string, unknown> {
  return (
    isObject(value) &&
    typeof value.name === 'string' &&
    Array.isArray(value.articles) &&
    value.articles.every(isArticle)
  );
}

export function parseGuestGuideResponse(response: unknown): GuestGuide {
  const result = isObject(response) ? response.guest_guide : undefined;
  const facts = isObject(result) ? result.property_facts : undefined;
  if (
    !isObject(response) ||
    response.status !== 200 ||
    !isObject(result) ||
    typeof result.available !== 'boolean' ||
    !isObject(result.guide) ||
    !Array.isArray(result.guide.sections) ||
    !result.guide.sections.every(
      (section) =>
        isArticleGroup(section) &&
        Array.isArray(section.subsections) &&
        section.subsections.every(isArticleGroup)
    ) ||
    !isObject(facts) ||
    !['entry_instructions', 'parking', 'laundry_facilities', 'directions', 'ge_ta_license'].every(
      (key) => facts[key] === undefined || typeof facts[key] === 'string'
    )
  ) {
    throw new Error('Invalid property guide response.');
  }
  return result as unknown as GuestGuide;
}
